import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    CalendarDays, ChevronLeft, ChevronRight, Loader2, Filter, X, AlertCircle,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import dict from "../i18n/es-MX";
import { useAuth } from "../contexts/AuthContext";

// ---------- date helpers ----------
function toISO(d) {
    return d.toISOString().slice(0, 10);
}
function fromISO(s) {
    return new Date(s + "T00:00:00");
}
function addDays(d, n) {
    const nd = new Date(d);
    nd.setDate(nd.getDate() + n);
    return nd;
}
function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfWeek(d) {
    // Monday=1..Sunday=0. We want Monday as start (Mexico standard? both fine — usar Lunes).
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    return addDays(d, diff);
}

const STATUS_COLORS = {
    in_use: "bg-emerald-500/25 hover:bg-emerald-500/40 border-emerald-500/40 text-emerald-100",
    rental: "bg-violet-500/25 hover:bg-violet-500/40 border-violet-500/40 text-violet-100",
    maintenance: "bg-amber-500/25 hover:bg-amber-500/40 border-amber-500/40 text-amber-100",
    out_of_service: "bg-red-500/25 border-red-500/40 text-red-100",
    available: "bg-slate-800/40 border-slate-800 text-slate-500",
};

const STATUS_DOT = {
    in_use: "bg-emerald-400",
    rental: "bg-violet-400",
    maintenance: "bg-amber-400",
    out_of_service: "bg-red-400",
    available: "bg-slate-600",
};

function cellNavTarget(cell) {
    if (cell.status === "in_use" || cell.status === "rental") return `/trabajos/${cell.job_id}`;
    if (cell.status === "maintenance") return `/mantenimiento`;
    return null;
}

function cellTooltip(cell) {
    const parts = [];
    if (cell.status === "in_use" || cell.status === "rental") {
        parts.push(`${cell.status === "rental" ? dict.calendar.tooltip.rental : dict.calendar.tooltip.job}: ${cell.job_code || ""}`);
        if (cell.client_name) parts.push(`${dict.calendar.tooltip.client}: ${cell.client_name}`);
    } else if (cell.status === "maintenance") {
        parts.push(dict.calendar.tooltip.maintenance);
        if (cell.maintenance_description) parts.push(cell.maintenance_description);
    } else if (cell.status === "out_of_service") {
        parts.push(dict.calendar.cellLabels.outOfService);
    } else {
        parts.push(dict.calendar.cellLabels.available);
    }
    parts.push(cell.date);
    return parts.join(" · ");
}

function formatPeriodLabel(view, anchor) {
    const m = dict.calendar.months[anchor.getMonth()];
    const y = anchor.getFullYear();
    if (view === "month") {
        return `${m} ${y}`;
    }
    if (view === "week") {
        const ws = startOfWeek(anchor);
        const we = addDays(ws, 6);
        const fromDay = ws.getDate();
        const toDay = we.getDate();
        if (ws.getMonth() === we.getMonth()) {
            return `${fromDay} – ${toDay} ${dict.calendar.months[ws.getMonth()]} ${ws.getFullYear()}`;
        }
        return `${fromDay} ${dict.calendar.months[ws.getMonth()]} – ${toDay} ${dict.calendar.months[we.getMonth()]} ${we.getFullYear()}`;
    }
    // day
    return `${dict.calendar.weekdaysLong[anchor.getDay()]} ${anchor.getDate()} de ${m} ${y}`;
}

function rangeFor(view, anchor) {
    if (view === "month") {
        return { from: toISO(startOfMonth(anchor)), to: toISO(endOfMonth(anchor)) };
    }
    if (view === "week") {
        const ws = startOfWeek(anchor);
        return { from: toISO(ws), to: toISO(addDays(ws, 6)) };
    }
    return { from: toISO(anchor), to: toISO(anchor) };
}

function navByView(view, anchor, dir) {
    if (view === "month") {
        const nd = new Date(anchor);
        nd.setMonth(nd.getMonth() + dir);
        return startOfMonth(nd);
    }
    if (view === "week") {
        return addDays(anchor, 7 * dir);
    }
    return addDays(anchor, dir);
}

// ============================================================
// Page
// ============================================================
export default function Calendar() {
    const { user, bootstrapping } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();

    // Operator redirect
    useEffect(() => {
        if (!bootstrapping && user && user.role === "operator") navigate("/campo", { replace: true });
    }, [user, bootstrapping, navigate]);

    const [view, setView] = useState(params.get("view") || "month");
    const [anchor, setAnchor] = useState(() => {
        const d = params.get("d");
        return d ? fromISO(d) : new Date();
    });

    // Filters
    const [allAssets, setAllAssets] = useState([]);
    const [selectedAssetIds, setSelectedAssetIds] = useState([]);
    const [selectedKinds, setSelectedKinds] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    // Sync URL
    useEffect(() => {
        const next = new URLSearchParams(params);
        next.set("view", view);
        next.set("d", toISO(anchor));
        setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, anchor]);

    // Load assets list for filter
    useEffect(() => {
        api.get("/assets").then(r => {
            const list = (r.data || []).filter(a => !a.archived);
            setAllAssets(list);
        }).catch(() => {});
    }, []);

    const { from, to } = useMemo(() => rangeFor(view, anchor), [view, anchor]);

    const loadSchedule = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const p = { from, to };
            if (selectedAssetIds.length > 0) p.asset_ids = selectedAssetIds.join(",");
            if (selectedKinds.length > 0) p.types = selectedKinds.join(",");
            const r = await api.get("/calendar/asset-schedule", { params: p });
            setSchedule(r.data);
        } catch (e) {
            setErr(formatApiError(e) || dict.calendar.loadError);
            setSchedule(null);
        } finally {
            setLoading(false);
        }
    }, [from, to, selectedAssetIds, selectedKinds]);

    useEffect(() => { loadSchedule(); }, [loadSchedule]);

    const toggleAsset = (id) => {
        setSelectedAssetIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    const toggleKind = (k) => {
        setSelectedKinds(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
    };
    const clearFilters = () => { setSelectedAssetIds([]); setSelectedKinds([]); };

    const hasFilters = selectedAssetIds.length > 0 || selectedKinds.length > 0;
    const rows = schedule?.rows || [];
    const days = schedule?.days || [];

    return (
        <div className="px-4 md:px-8 py-6 space-y-5" data-testid="calendar-page">
            {/* Header */}
            <header className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
                        <CalendarDays size={22} className="text-brand-orange"/>
                        {dict.calendar.title}
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">{dict.calendar.subtitle}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    {/* View toggle */}
                    <div className="inline-flex rounded-lg border border-slate-800 overflow-hidden" data-testid="calendar-view-toggle">
                        {["month", "week", "day"].map(v => (
                            <button key={v} type="button" onClick={() => setView(v)} data-testid={`calendar-view-${v}`}
                                className={`px-3 h-9 text-xs uppercase tracking-wider font-semibold transition ${view===v ? "bg-brand-orange text-slate-950" : "bg-slate-950 text-slate-300 hover:bg-slate-900"}`}>
                                {dict.calendar.views[v]}
                            </button>
                        ))}
                    </div>
                    {/* Period nav */}
                    <div className="flex items-center gap-1">
                        <button type="button" onClick={() => setAnchor(navByView(view, anchor, -1))} data-testid="calendar-prev"
                            className="h-9 w-9 rounded-lg border border-slate-800 bg-slate-950 hover:bg-slate-900 inline-flex items-center justify-center">
                            <ChevronLeft size={16}/>
                        </button>
                        <button type="button" onClick={() => setAnchor(new Date())} data-testid="calendar-today"
                            className="h-9 px-3 rounded-lg border border-slate-800 bg-slate-950 hover:bg-slate-900 text-xs uppercase tracking-wider">
                            {dict.calendar.today}
                        </button>
                        <button type="button" onClick={() => setAnchor(navByView(view, anchor, +1))} data-testid="calendar-next"
                            className="h-9 w-9 rounded-lg border border-slate-800 bg-slate-950 hover:bg-slate-900 inline-flex items-center justify-center">
                            <ChevronRight size={16}/>
                        </button>
                        <span className="ml-2 text-sm font-medium text-slate-200 min-w-0" data-testid="calendar-period-label">
                            {formatPeriodLabel(view, anchor)}
                        </span>
                    </div>
                </div>
            </header>

            {/* Filters bar */}
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                <div className="flex items-center justify-between mb-2">
                    <button type="button" onClick={() => setShowFilters(s => !s)} data-testid="calendar-filters-toggle"
                        className="text-xs uppercase tracking-wider text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
                        <Filter size={14}/> {showFilters ? dict.calendar.filters.hideFilters : dict.calendar.filters.showFilters}
                        {hasFilters && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-orange text-slate-950 text-[10px] font-bold">{selectedAssetIds.length + selectedKinds.length}</span>}
                    </button>
                    {hasFilters && (
                        <button type="button" onClick={clearFilters} data-testid="calendar-clear-filters"
                            className="text-xs text-slate-400 hover:text-red-300 inline-flex items-center gap-1">
                            <X size={12}/> {dict.calendar.filters.clear}
                        </button>
                    )}
                </div>
                {showFilters && (
                    <div className="space-y-3 pt-2 border-t border-slate-800" data-testid="calendar-filters-panel">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{dict.calendar.filters.types}</div>
                            <div className="flex gap-2 flex-wrap">
                                {["camion", "maquinaria"].map(k => (
                                    <button key={k} type="button" onClick={() => toggleKind(k)} data-testid={`calendar-kind-${k}`}
                                        className={`px-3 h-8 rounded-full border text-xs ${selectedKinds.includes(k) ? "bg-brand-orange text-slate-950 border-brand-orange font-semibold" : "bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                                        {k === "camion" ? dict.calendar.filters.kindCamion : dict.calendar.filters.kindMaquinaria}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{dict.calendar.filters.assets}</div>
                            <div className="flex gap-2 flex-wrap">
                                {allAssets.map(a => (
                                    <button key={a.id} type="button" onClick={() => toggleAsset(a.id)} data-testid={`calendar-asset-${a.id}`}
                                        className={`px-3 h-8 rounded-full border text-xs ${selectedAssetIds.includes(a.id) ? "bg-brand-orange text-slate-950 border-brand-orange font-semibold" : "bg-slate-950 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
                                        {a.nickname}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* Legend */}
            <section className="flex flex-wrap gap-3 text-xs text-slate-300" data-testid="calendar-legend">
                {[
                    ["in_use", dict.calendar.legend.inUse],
                    ["rental", dict.calendar.legend.rental],
                    ["maintenance", dict.calendar.legend.maintenance],
                    ["out_of_service", dict.calendar.legend.outOfService],
                    ["available", dict.calendar.legend.available],
                ].map(([k, label]) => (
                    <div key={k} className="inline-flex items-center gap-2">
                        <span className={`inline-block w-3 h-3 rounded ${STATUS_DOT[k]}`}/>
                        <span>{label}</span>
                    </div>
                ))}
            </section>

            {/* Body */}
            {loading ? (
                <div className="py-12 text-center text-slate-400" data-testid="calendar-loading">
                    <Loader2 className="inline animate-spin mr-2" size={16}/>{dict.calendar.loading}
                </div>
            ) : err ? (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200 inline-flex items-center gap-2" data-testid="calendar-error">
                    <AlertCircle size={16}/> {err}
                </div>
            ) : rows.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm" data-testid="calendar-empty">
                    {hasFilters ? dict.calendar.empty.noFilterMatch : dict.calendar.empty.noActivity}
                </div>
            ) : view === "day" ? (
                <DayView day={days[0]} rows={rows} navigate={navigate}/>
            ) : view === "week" ? (
                <WeekView days={days} rows={rows} navigate={navigate}/>
            ) : (
                <MonthView days={days} rows={rows} navigate={navigate}/>
            )}
        </div>
    );
}

// ============================================================
// MonthView — compact grid (small cells)
// ============================================================
function MonthView({ days, rows, navigate }) {
    return (
        <div className="rounded-xl border border-slate-800 overflow-x-auto" data-testid="calendar-month-grid">
            <div className="inline-block min-w-full">
                {/* Header row */}
                <div className="flex border-b border-slate-800 bg-slate-900/60 sticky top-0 z-10">
                    <div className="w-44 shrink-0 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-r border-slate-800">
                        {dict.calendar.headers.asset}
                    </div>
                    {days.map(d => {
                        const dd = fromISO(d);
                        const isToday = toISO(new Date()) === d;
                        const isWeekend = dd.getDay() === 0 || dd.getDay() === 6;
                        return (
                            <div key={d} className={`w-9 shrink-0 text-center py-1 border-r border-slate-800 ${isToday ? "bg-brand-orange/20" : isWeekend ? "bg-slate-800/40" : ""}`}>
                                <div className="text-[9px] text-slate-500 uppercase">{dict.calendar.weekdaysShort[dd.getDay()]}</div>
                                <div className={`text-[11px] font-mono ${isToday ? "font-bold text-brand-orange" : "text-slate-300"}`}>{dd.getDate()}</div>
                            </div>
                        );
                    })}
                </div>
                {/* Asset rows */}
                {rows.map(row => (
                    <div key={row.asset_id} className="flex border-b border-slate-800 last:border-b-0" data-testid={`calendar-row-${row.asset_id}`}>
                        <Link to={`/activos/${row.asset_id}`} className="w-44 shrink-0 px-3 py-2 border-r border-slate-800 hover:bg-slate-900/60">
                            <div className="text-sm font-medium text-slate-100 truncate">{row.nickname}</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-wider truncate">{row.subtype}</div>
                        </Link>
                        {row.cells.map(cell => {
                            const target = cellNavTarget(cell);
                            const Component = target ? "button" : "div";
                            return (
                                <Component key={cell.date} type={target ? "button" : undefined} onClick={() => target && navigate(target)}
                                    title={cellTooltip(cell)} data-testid={`calendar-cell-${row.asset_id}-${cell.date}`}
                                    className={`w-9 h-10 shrink-0 border-r border-slate-800 ${STATUS_COLORS[cell.status]} ${target ? "cursor-pointer" : "cursor-default"}`}>
                                </Component>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================================
// WeekView — bigger cells with mini-card content
// ============================================================
function WeekView({ days, rows, navigate }) {
    return (
        <div className="rounded-xl border border-slate-800 overflow-x-auto" data-testid="calendar-week-grid">
            <div className="inline-block min-w-full">
                <div className="flex border-b border-slate-800 bg-slate-900/60">
                    <div className="w-44 shrink-0 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-r border-slate-800">
                        {dict.calendar.headers.asset}
                    </div>
                    {days.map(d => {
                        const dd = fromISO(d);
                        const isToday = toISO(new Date()) === d;
                        return (
                            <div key={d} className={`flex-1 min-w-[120px] text-center py-2 border-r border-slate-800 ${isToday ? "bg-brand-orange/20" : ""}`}>
                                <div className="text-[10px] text-slate-500 uppercase">{dict.calendar.weekdaysLong[dd.getDay()]}</div>
                                <div className={`text-sm font-mono ${isToday ? "font-bold text-brand-orange" : "text-slate-200"}`}>
                                    {dd.getDate()} {dict.calendar.months[dd.getMonth()].slice(0,3)}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {rows.map(row => (
                    <div key={row.asset_id} className="flex border-b border-slate-800 last:border-b-0" data-testid={`calendar-row-${row.asset_id}`}>
                        <Link to={`/activos/${row.asset_id}`} className="w-44 shrink-0 px-3 py-3 border-r border-slate-800 hover:bg-slate-900/60">
                            <div className="text-sm font-medium text-slate-100 truncate">{row.nickname}</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-wider truncate">{row.subtype}</div>
                        </Link>
                        {row.cells.map(cell => {
                            const target = cellNavTarget(cell);
                            return (
                                <button key={cell.date} type="button" onClick={() => target && navigate(target)}
                                    disabled={!target} title={cellTooltip(cell)}
                                    data-testid={`calendar-cell-${row.asset_id}-${cell.date}`}
                                    className={`flex-1 min-w-[120px] h-20 border-r border-slate-800 text-left px-2 py-1 ${STATUS_COLORS[cell.status]} ${target ? "cursor-pointer" : "cursor-default"}`}>
                                    {(cell.status === "in_use" || cell.status === "rental") && (
                                        <div className="text-[10px] leading-tight">
                                            <div className="font-mono font-semibold truncate">{cell.job_code}</div>
                                            <div className="text-[10px] opacity-80 truncate">{cell.client_name || "—"}</div>
                                        </div>
                                    )}
                                    {cell.status === "maintenance" && (
                                        <div className="text-[10px] leading-tight">
                                            <div className="font-semibold">{dict.calendar.cellLabels.maintenance}</div>
                                            <div className="opacity-80 truncate">{cell.maintenance_description}</div>
                                        </div>
                                    )}
                                    {cell.status === "out_of_service" && (
                                        <div className="text-[10px] font-semibold">{dict.calendar.cellLabels.outOfService}</div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================================
// DayView — vertical list, one row per asset
// ============================================================
function DayView({ day, rows, navigate }) {
    return (
        <div className="rounded-xl border border-slate-800 overflow-hidden" data-testid="calendar-day-list">
            <ul>
                {rows.map((row, idx) => {
                    const cell = row.cells[0]; // day view = 1 day
                    const target = cellNavTarget(cell);
                    let detail;
                    if (cell.status === "in_use" || cell.status === "rental") {
                        detail = <span><span className="font-mono text-xs text-slate-400">{cell.job_code}</span> · {cell.client_name || "—"}</span>;
                    } else if (cell.status === "maintenance") {
                        detail = <span>{dict.calendar.cellLabels.maintenance}: {cell.maintenance_description}</span>;
                    } else if (cell.status === "out_of_service") {
                        detail = <span>{dict.calendar.cellLabels.outOfService}</span>;
                    } else {
                        detail = <span className="text-slate-500">{dict.calendar.cellLabels.available}</span>;
                    }
                    const labelByStatus = {
                        in_use: dict.calendar.legend.inUse,
                        rental: dict.calendar.legend.rental,
                        maintenance: dict.calendar.legend.maintenance,
                        out_of_service: dict.calendar.legend.outOfService,
                        available: dict.calendar.legend.available,
                    };
                    return (
                        <li key={row.asset_id} data-testid={`calendar-day-row-${row.asset_id}`}
                            className={`flex items-center justify-between gap-3 px-4 py-3 ${idx > 0 ? "border-t border-slate-800" : ""} hover:bg-slate-900/40`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${STATUS_DOT[cell.status]}`}/>
                                <div className="min-w-0">
                                    <Link to={`/activos/${row.asset_id}`} className="text-sm font-medium text-slate-100 hover:text-brand-orange truncate block">
                                        {row.nickname} <span className="text-[10px] text-slate-500 uppercase">{row.subtype}</span>
                                    </Link>
                                    <div className="text-xs text-slate-300 truncate">{detail}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_COLORS[cell.status]}`}>
                                    {labelByStatus[cell.status]}
                                </span>
                                {target && (
                                    <button type="button" onClick={() => navigate(target)} data-testid={`calendar-day-open-${row.asset_id}`}
                                        className="text-xs text-brand-orange hover:underline">
                                        Abrir →
                                    </button>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
